import { LoadingOutlined } from "@ant-design/icons";
import distance from "@turf/distance";
import { point } from "@turf/helpers";
import {
    boundingBox,
    IDetectedBarcode,
    Scanner,
} from "@yudiel/react-qr-scanner";
import { App, Flex, Spin } from "antd";
import dayjs from "dayjs";
import { FC, useEffect, useRef, useState } from "react";
import { backendDateFormat } from "tm-shared/constants";
import { VerifierQrPayloadWithScanMeta } from "tm-shared/types";
import { isValidVerifierQrPayload } from "tm-shared/utils";

import { useAppDispatch } from "@/hooks/redux";
import BaseButton from "@/shared/ui/BaseButton";
import { BaseModal } from "@/shared/ui/BaseModal";
import { PrimaryText } from "@/shared/ui/Typography";
import { setSupervisorCheck } from "@/store/slices/work";
import { getPreciseLocation } from "@/utils/geo/geo";

import { StyledScannerContainer } from "./Styled";

interface SVScanModalProps {
    open: boolean;
    taskId: string;
    pointId?: string;
    onClose: () => void;
}

const MAX_DISTANCE_M = 5;

const decodeB64Json = (b64: string): unknown | null => {
    try {
        const clean = b64.trim().replace(/^data:.*;base64,/, "");
        const bytes = Uint8Array.from(atob(clean), (c) => c.charCodeAt(0));
        const json = new TextDecoder("utf-8").decode(bytes);
        return JSON.parse(json);
    } catch (error) {
        console.error("Failed to decode QR payload:", error);
        return null;
    }
};

const getCameraErrorMessage = (error: unknown): string => {
    const err = error as Partial<Error> | undefined;
    const message = err?.message?.toLowerCase() || "";
    const name = err?.name?.toLowerCase?.() || "";

    if (
        message.includes("permission") ||
        message.includes("denied") ||
        name === "notallowederror"
    ) {
        return "Немає доступу до камери. Надайте дозвіл у налаштуваннях браузера";
    }

    if (
        message.includes("in use") ||
        message.includes("already") ||
        name === "notreadableerror"
    ) {
        return "Камера зайнята іншим додатком";
    }

    if (
        message.includes("not found") ||
        message.includes("no camera") ||
        name === "notfounderror"
    ) {
        return "Камеру не знайдено";
    }

    return err?.message || "Не вдалося запустити камеру";
};

const extractTextFromScanResult = (
    result: IDetectedBarcode[],
): string | null => {
    if (!result) return null;

    const stringResult = result[0]?.rawValue;

    if (stringResult) return stringResult;

    return null;
};

export const SVScanModal: FC<SVScanModalProps> = ({
    open,
    taskId,
    pointId,
    onClose,
}) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();

    const [geoLoading, setGeoLoading] = useState(false);
    const [distMeters, setDistMeters] = useState<number | null>(null);
    const [pendingPayload, setPendingPayload] =
        useState<VerifierQrPayloadWithScanMeta | null>(null);

    const lastTextRef = useRef<string | null>(null);
    const isMountedRef = useRef(true);

    const handleCancel = () => {
        resetState();
        onClose();
    };

    const resetState = () => {
        setDistMeters(null);
        setPendingPayload(null);
        lastTextRef.current = null;
    };

    const finishSuccess = (
        payload: VerifierQrPayloadWithScanMeta,
        forced = false,
    ) => {
        dispatch(setSupervisorCheck({ taskId, payload }));

        notification.success({
            title: forced
                ? "Підтверджено (з попередженням)"
                : "QR підтверджено",
        });

        onClose();
    };

    const handleComplete = async (string: string) => {
        if (lastTextRef.current === string) return;
        lastTextRef.current = string;

        const payload = decodeB64Json(string);

        if (!payload || !isValidVerifierQrPayload(payload)) {
            notification.error({
                title: "Невірний формат QR-коду",
                duration: 5,
            });
            lastTextRef.current = null;
            handleCancel();
            return;
        }

        if (payload.pointId !== pointId) {
            notification.error({
                title: "Маркет Qr-коду не відповідає маркету задачі",
                duration: 5,
            });
            lastTextRef.current = null;
            handleCancel();
            return;
        }

        const scannedAt = dayjs().format(backendDateFormat.dateTime);

        try {
            setGeoLoading(true);
            const agentCoords = await getPreciseLocation();
            if (!isMountedRef.current) return;

            const myPoint = point([agentCoords.lng, agentCoords.lat]);
            const svPoint = point([payload.geo.lng, payload.geo.lat]);

            const dist = distance(myPoint, svPoint, { units: "meters" });

            setDistMeters(dist);

            const payloadWithMeta: VerifierQrPayloadWithScanMeta = {
                ...payload,
                scannedAt,
                distance: Math.round(dist),
            };

            if (dist > MAX_DISTANCE_M) {
                setPendingPayload(payloadWithMeta);
            } else {
                finishSuccess(payloadWithMeta);
            }
        } catch (error) {
            const geoErr = error as GeolocationPositionError;
            if (geoErr && typeof geoErr === "object" && "code" in geoErr) {
                if (geoErr.code === 1) {
                    notification.error({
                        title: "Не вдалося отримати вашу геолокацію. Дозвольте доступ та спробуйте ще раз.",
                    });
                    resetState();
                    return;
                }
            }

            notification.error({
                title:
                    error instanceof Error
                        ? error.message
                        : "Не вдалося визначити координати",
                duration: 5,
            });

            resetState();
        } finally {
            if (isMountedRef.current) {
                setGeoLoading(false);
            }
        }
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (open) {
            lastTextRef.current = null;
        }
    }, [open]);

    const handleConfirm = () => {
        if (!pendingPayload) return;
        finishSuccess(pendingPayload, true);
    };

    const geoWarning = distMeters && distMeters > MAX_DISTANCE_M;
    const showScanner = open && !geoLoading && !geoWarning;

    return (
        <BaseModal
            open={open}
            onClose={handleCancel}
            title="Перевірка супервайзером"
            actions={null}
            destroyOnHidden
        >
            {geoLoading ? (
                <Flex vertical gap={12} align="center">
                    <Spin size="large" indicator={<LoadingOutlined spin />} />
                    <PrimaryText textColor="disabled">
                        Визначення координат...
                    </PrimaryText>
                </Flex>
            ) : geoWarning ? (
                <Flex vertical gap={12}>
                    <PrimaryText>
                        Відстань до агента{" "}
                        <b>
                            {distMeters !== null
                                ? `${Math.round(distMeters)}м`
                                : "—"}
                        </b>
                        , максимально допустимо — <b>{MAX_DISTANCE_M}м</b>. Ви
                        впевнені? Це буде зафіксовано!
                    </PrimaryText>

                    <Flex gap={12} vertical>
                        <BaseButton type="default" onClick={handleCancel}>
                            Скасувати
                        </BaseButton>

                        <BaseButton type="primary" onClick={handleConfirm}>
                            Все одно продовжити
                        </BaseButton>
                    </Flex>
                </Flex>
            ) : (
                <>
                    <StyledScannerContainer>
                        {showScanner ? (
                            <Scanner
                                onScan={(result) => {
                                    const text =
                                        extractTextFromScanResult(result);

                                    if (!text) return;

                                    handleComplete(text);
                                }}
                                onError={(error) => {
                                    console.error("Camera error:", error);
                                    notification.error({
                                        title: getCameraErrorMessage(error),
                                        duration: 5,
                                    });
                                }}
                                constraints={{ facingMode: "environment" }}
                                formats={["qr_code"]}
                                components={{ tracker: boundingBox }}
                            />
                        ) : null}
                    </StyledScannerContainer>

                    <PrimaryText textAlign="center" textColor="disabled">
                        Наведіть камеру на QR-код
                    </PrimaryText>
                </>
            )}
        </BaseModal>
    );
};
